import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { settingsApi, headerFooterApi } from "@/lib/api";

// =====================================================
// TYPES
// =====================================================
export interface ContactSettings {
  phone: string;
  phone2: string;
  email: string;
  address: string;
  whatsappNumber: string;
}

export interface HeaderSettings {
  brandName1: string;
  brandName2: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  siteLogo: string;
  siteIcon: string;
  favicon: string;
  showLogoInsteadOfText: boolean;
}

export interface SocialLinks {
  facebook: string;
  instagram: string;
  linkedin: string;
  youtube: string;
}

export interface FooterSettings {
  brandName1: string;
  brandName2: string;
  description: string;
  copyrightText: string;
  disclaimerText: string;
  socialLinks: SocialLinks;
  footerLogo: string;
  showLogoInsteadOfText: boolean;
}

export interface SiteSettings {
  contact: ContactSettings;
  header: HeaderSettings;
  footer: FooterSettings;
}

// =====================================================
// DEFAULTS (match database values)
// =====================================================
const DEFAULT_CONTACT: ContactSettings = {
  phone: "+91 7668855153",
  phone2: "+91 98765 43211",
  email: "swipeandkey@gmail.com",
  address: "Block-3/R3 113, M3M Corner walk, Sector 74, Golf Course Extension Road, Sector 73, Gurugram, Haryana 122101",
  whatsappNumber: "+91 7668855153",
};

const DEFAULT_HEADER: HeaderSettings = {
  brandName1: "SWIPE",
  brandName2: "AND KEY",
  ctaButtonText: "Get Consultation",
  ctaButtonLink: "/contact",
  siteLogo: "",
  siteIcon: "",
  favicon: "",
  showLogoInsteadOfText: false,
};

const DEFAULT_FOOTER: FooterSettings = {
  brandName1: "SWIPE",
  brandName2: "AND KEY",
  description: "Your trusted real estate partner. With years of experience, we help you find premium properties and spaces tailored to your needs.",
  copyrightText: "Swipe And Key. All rights reserved.",
  disclaimerText: "All listings are subject to availability & verification.",
  socialLinks: { facebook: "https://facebook.com/swipeandkey", instagram: "https://instagram.com/swipeandkey", linkedin: "https://linkedin.com/company/swipeandkey", youtube: "https://youtube.com/@swipeandkey" },
  footerLogo: "",
  showLogoInsteadOfText: false,
};

// =====================================================
// HELPERS — map flat API response → structured settings
// =====================================================
function mapApiToSettings(apiData: Record<string, Record<string, { value: string | boolean }>>): Partial<SiteSettings> {
  const result: Partial<SiteSettings> = {};

  // Contact
  const c = apiData.contact;
  if (c) {
    result.contact = {
      phone: (c.contact_phone?.value as string) || DEFAULT_CONTACT.phone,
      phone2: (c.contact_phone_2?.value as string) || DEFAULT_CONTACT.phone2,
      email: (c.contact_email?.value as string) || DEFAULT_CONTACT.email,
      address: (c.contact_address?.value as string) || DEFAULT_CONTACT.address,
      whatsappNumber: (c.whatsapp_number?.value as string) || DEFAULT_CONTACT.whatsappNumber,
    };
  }

  // Social links from API social group
  const s = apiData.social;
  const socialLinks: SocialLinks = {
    facebook: (s?.facebook_url?.value as string) || DEFAULT_FOOTER.socialLinks.facebook,
    instagram: (s?.instagram_url?.value as string) || DEFAULT_FOOTER.socialLinks.instagram,
    linkedin: (s?.linkedin_url?.value as string) || DEFAULT_FOOTER.socialLinks.linkedin,
    youtube: (s?.youtube_url?.value as string) || DEFAULT_FOOTER.socialLinks.youtube,
  };

  // Branding from API branding group
  const b = apiData.branding;
  const siteLogo = (b?.site_logo?.value as string) || "";
  const siteIcon = (b?.site_icon?.value as string) || "";
  const favicon = (b?.favicon_url?.value as string) || "";

  // Header — don't force showLogoInsteadOfText; let DB value decide
  result.header = {
    ...DEFAULT_HEADER,
    siteLogo,
    siteIcon,
    favicon,
  };

  // Footer
  result.footer = {
    ...DEFAULT_FOOTER,
    socialLinks,
    footerLogo: siteLogo,
  };

  return result;
}

function mergeWithDefaults(partial: Partial<SiteSettings>): SiteSettings {
  return {
    contact: { ...DEFAULT_CONTACT, ...partial.contact },
    header: { ...DEFAULT_HEADER, ...partial.header },
    footer: {
      ...DEFAULT_FOOTER,
      ...partial.footer,
      socialLinks: { ...DEFAULT_FOOTER.socialLinks, ...(partial.footer?.socialLinks || {}) },
    },
  };
}

// =====================================================
// CONTEXT
// =====================================================
interface SiteSettingsContextType {
  settings: SiteSettings;
  updateContact: (contact: Partial<ContactSettings>) => void;
  updateHeader: (header: Partial<HeaderSettings>) => void;
  updateFooter: (footer: Partial<FooterSettings>) => void;
  updateFooterSocial: (social: Partial<SocialLinks>) => void;
  saveSettings: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextType | null>(null);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(() => mergeWithDefaults({}));
  const [isLoaded, setIsLoaded] = useState(false);

  // On mount — fetch from API (single source of truth)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch both settings and header/footer in parallel
        const [settingsRes, hfRes] = await Promise.all([
          settingsApi.getAll(),
          headerFooterApi.get(),
        ]);

        if (cancelled) return;

        let mapped: Partial<SiteSettings> = {};
        if (settingsRes.success && settingsRes.data) {
          mapped = mapApiToSettings(settingsRes.data as unknown as Record<string, Record<string, { value: string | boolean }>>);
        }

        // Overlay header_footer_settings on top
        if (hfRes.success && hfRes.data) {
          const hfData = hfRes.data as { header?: Record<string, unknown>; footer?: Record<string, unknown> };
          const hf = hfData;

          if (hf.header && Object.keys(hf.header).length > 0) {
            mapped.header = {
              ...(mapped.header || DEFAULT_HEADER),
              brandName1: (hf.header.brandName1 as string) || mapped.header?.brandName1 || DEFAULT_HEADER.brandName1,
              brandName2: (hf.header.brandName2 as string) || mapped.header?.brandName2 || DEFAULT_HEADER.brandName2,
              ctaButtonText: (hf.header.ctaButtonText as string) || mapped.header?.ctaButtonText || DEFAULT_HEADER.ctaButtonText,
              ctaButtonLink: (hf.header.ctaButtonLink as string) || mapped.header?.ctaButtonLink || DEFAULT_HEADER.ctaButtonLink,
              siteLogo: (hf.header.siteLogo as string) || mapped.header?.siteLogo || DEFAULT_HEADER.siteLogo,
              siteIcon: (hf.header.siteIcon as string) || mapped.header?.siteIcon || DEFAULT_HEADER.siteIcon,
              favicon: (hf.header.favicon as string) || mapped.header?.favicon || DEFAULT_HEADER.favicon,
              showLogoInsteadOfText: hf.header.showLogoInsteadOfText === 'true' || hf.header.showLogoInsteadOfText === true ? true : false,
            };
          }

          if (hf.footer && Object.keys(hf.footer).length > 0) {
            const socialLinksFromHF = hf.footer.socialLinks;
            const parsedSocial = typeof socialLinksFromHF === 'object' && socialLinksFromHF
              ? socialLinksFromHF as Partial<SocialLinks>
              : {};

            mapped.footer = {
              ...(mapped.footer || DEFAULT_FOOTER),
              brandName1: (hf.footer.brandName1 as string) || mapped.footer?.brandName1 || DEFAULT_FOOTER.brandName1,
              brandName2: (hf.footer.brandName2 as string) || mapped.footer?.brandName2 || DEFAULT_FOOTER.brandName2,
              description: (hf.footer.description as string) || mapped.footer?.description || DEFAULT_FOOTER.description,
              copyrightText: (hf.footer.copyrightText as string) || mapped.footer?.copyrightText || DEFAULT_FOOTER.copyrightText,
              disclaimerText: (hf.footer.disclaimerText as string) || mapped.footer?.disclaimerText || DEFAULT_FOOTER.disclaimerText,
              footerLogo: (hf.footer.footerLogo as string) || mapped.footer?.footerLogo || DEFAULT_FOOTER.footerLogo,
              showLogoInsteadOfText: hf.footer.showLogoInsteadOfText === 'true' || hf.footer.showLogoInsteadOfText === true ? true : false,
              socialLinks: {
                ...DEFAULT_FOOTER.socialLinks,
                ...(mapped.footer?.socialLinks || {}),
                ...parsedSocial,
              },
            };
          }
        }

        const merged = mergeWithDefaults(mapped);
        setSettings(merged);
      } catch {
        // API unreachable — use defaults
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Update favicon dynamically
  useEffect(() => {
    if (settings.header.favicon) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = settings.header.favicon;
    }
  }, [settings.header.favicon]);

  const updateContact = useCallback((partial: Partial<ContactSettings>) => {
    setSettings((prev) => ({ ...prev, contact: { ...prev.contact, ...partial } }));
  }, []);

  const updateHeader = useCallback((partial: Partial<HeaderSettings>) => {
    setSettings((prev) => ({ ...prev, header: { ...prev.header, ...partial } }));
  }, []);

  const updateFooter = useCallback((partial: Partial<FooterSettings>) => {
    setSettings((prev) => ({ ...prev, footer: { ...prev.footer, ...partial } }));
  }, []);

  const updateFooterSocial = useCallback((social: Partial<SocialLinks>) => {
    setSettings((prev) => ({
      ...prev,
      footer: { ...prev.footer, socialLinks: { ...prev.footer.socialLinks, ...social } },
    }));
  }, []);

  // Save to API + update cache
  const saveSettings = useCallback(async () => {
    // Build flat key-value map matching the database `setting_key` column
    const apiPayload: Record<string, string | boolean> = {
      contact_phone: settings.contact.phone,
      contact_phone_2: settings.contact.phone2,
      contact_email: settings.contact.email,
      contact_address: settings.contact.address,
      whatsapp_number: settings.contact.whatsappNumber,
      facebook_url: settings.footer.socialLinks.facebook,
      instagram_url: settings.footer.socialLinks.instagram,
      linkedin_url: settings.footer.socialLinks.linkedin,
      youtube_url: settings.footer.socialLinks.youtube,
    };
    apiPayload.site_logo = settings.header.siteLogo;
    apiPayload.site_icon = settings.header.siteIcon;
    apiPayload.favicon_url = settings.header.favicon;

    // Build header/footer payload for header_footer_settings table
    const hfPayload = {
      header: {
        brandName1: settings.header.brandName1,
        brandName2: settings.header.brandName2,
        ctaButtonText: settings.header.ctaButtonText,
        ctaButtonLink: settings.header.ctaButtonLink,
        siteLogo: settings.header.siteLogo,
        siteIcon: settings.header.siteIcon,
        favicon: settings.header.favicon,
        showLogoInsteadOfText: settings.header.showLogoInsteadOfText,
      },
      footer: {
        brandName1: settings.footer.brandName1,
        brandName2: settings.footer.brandName2,
        description: settings.footer.description,
        copyrightText: settings.footer.copyrightText,
        disclaimerText: settings.footer.disclaimerText,
        footerLogo: settings.footer.footerLogo,
        showLogoInsteadOfText: settings.footer.showLogoInsteadOfText,
        socialLinks: settings.footer.socialLinks,
      },
    };

    try {
      await Promise.all([
        settingsApi.update(apiPayload),
        headerFooterApi.update(hfPayload),
      ]);
    } catch {
      // API unreachable — settings will revert on next page load
    }
  }, [settings]);

  return (
    <SiteSettingsContext.Provider value={{ settings, updateContact, updateHeader, updateFooter, updateFooterSocial, saveSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error("useSiteSettings must be used within SiteSettingsProvider");
  return ctx;
}
